import { useState } from "react";
import { ConfirmDeleteButton } from "@/components/ui/confirm-delete-button";
import { fetchTractorDeleteImpact, TractorDeleteImpact } from "@/hooks/useTractorDeletion";

interface TractorDeleteButtonProps {
  tractorId: string;
  tractorName: string;
  onConfirm: () => void;
  isPending?: boolean;
}

/**
 * Delete control for a tractor that previews the assignment impact before
 * hard-deleting (orders/legs/templates lose their truck assignment).
 */
export function TractorDeleteButton({
  tractorId,
  tractorName,
  onConfirm,
  isPending,
}: TractorDeleteButtonProps) {
  const [impact, setImpact] = useState<TractorDeleteImpact | null>(null);
  const [loading, setLoading] = useState(false);

  const handleOpenChange = async (open: boolean) => {
    if (!open) return;
    setImpact(null);
    setLoading(true);
    try {
      setImpact(await fetchTractorDeleteImpact(tractorId));
    } catch {
      setImpact(null);
    } finally {
      setLoading(false);
    }
  };

  const description = (
    <>
      <p>
        Permanently delete <span className="font-medium text-foreground">{tractorName}</span>? This
        cannot be undone.
      </p>
      {loading ? (
        <p>Checking assignments…</p>
      ) : impact ? (
        impact.inTransit > 0 ? (
          <p className="text-destructive">
            This tractor is assigned to {impact.inTransit} in-transit order(s). Reassign them before
            deleting.
          </p>
        ) : (
          <ul className="list-disc pl-4 space-y-0.5">
            <li>{impact.orders} order(s) will lose this truck assignment</li>
            <li>{impact.legs} leg(s) will lose this truck assignment</li>
            {impact.templates + impact.templateLegs > 0 && (
              <li>
                {impact.templates + impact.templateLegs} recurring template row(s) will be unassigned
              </li>
            )}
            <li>Its service, maintenance, work order and timeline records will be removed</li>
          </ul>
        )
      ) : null}
    </>
  );

  return (
    <ConfirmDeleteButton
      entityName="tractor"
      entityLabel={tractorName}
      description={description}
      onOpenChange={handleOpenChange}
      onConfirm={onConfirm}
      isPending={isPending}
    />
  );
}
